import React, { useState, useEffect } from 'react';
import {
  Hash, Plus, Trash2, Edit2, Sparkles, Copy, CheckCircle, X, MessageSquare,
  Calendar, Settings, BarChart3, Link as LinkIcon,
  Clock, TrendingUp, ThumbsUp, MessageCircle, Share2, Eye, Send,
  ChevronDown, ChevronUp, Image, Square, CheckSquare
} from 'lucide-react';

export default function LinkedInAutomation() {
  const [keywords, setKeywords] = useState([]);
  const [posts, setPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]); // All posts for management
  const [schedules, setSchedules] = useState([]);
  const [linkedinStatus, setLinkedinStatus] = useState({ connected: false });
  const [analytics, setAnalytics] = useState(null);

  const [activeTab, setActiveTab] = useState('overview');
  const [showAddKeyword, setShowAddKeyword] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // State for post management
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [imagePickerForPost, setImagePickerForPost] = useState(null); // Track which post is selecting an image
  const [loadingImages, setLoadingImages] = useState(false); // Track image loading state

  // Form states
  const [newKeyword, setNewKeyword] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [withVariations, setWithVariations] = useState(true);
  const [withImages, setWithImages] = useState(false);

  // Schedule form
  const [scheduleKeywordId, setScheduleKeywordId] = useState('');
  const [intervalType, setIntervalType] = useState('daily');
  const [intervalValue, setIntervalValue] = useState(1);
  const [timeOfDay, setTimeOfDay] = useState('09:00');
  const [useRandomTime, setUseRandomTime] = useState(false);
  const [randomTimeStart, setRandomTimeStart] = useState('09:00');
  const [randomTimeEnd, setRandomTimeEnd] = useState('17:00');
  const [selectedDays, setSelectedDays] = useState([1, 2, 3, 4, 5]); // Mon-Fri
  const [timezone, setTimezone] = useState('Asia/Kolkata');

  // Keyword search/filter
  const [keywordSearch, setKeywordSearch] = useState('');
  const [keywordCategoryFilter, setKeywordCategoryFilter] = useState('all');

  // Generate modal keyword selection
  const [generateKeywordId, setGenerateKeywordId] = useState('');
  const [generateKeywordSearch, setGenerateKeywordSearch] = useState('');

  // Notification email edit (for Settings tab)
  const [showEmailEdit, setShowEmailEdit] = useState(false);

  // Preferences form
  const [prefForm, setPrefForm] = useState({
    default_tone: 'professional',
    default_length: 'medium',
    default_hashtag_count: 5,
    enable_emojis: true,
    enable_multi_variation: true,
    variation_count: 3,
    auto_approve: false,
    notification_enabled: true,
    notification_email: '',
    timezone: 'Asia/Kolkata',
    posting_hours_start: '09:00',
    posting_hours_end: '18:00',
    max_posts_per_day: 3
  });

  const api = process.env.REACT_APP_API_BASE || '';
  const token = localStorage.getItem('token');

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          loadLinkedInStatus(),
          loadKeywords(),
          loadPosts(),
          loadAllPosts(),
          loadSchedules(),
          loadPreferences(),
          loadAnalytics()
        ]);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    loadData();
  }, []);

  const loadLinkedInStatus = async () => {
    try {
      const res = await fetch(`${api}/api/linkedin/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        setLinkedinStatus({ connected: false });
        return;
      }
      const data = await res.json();
      setLinkedinStatus(data);
    } catch (error) {
      console.error('Error loading LinkedIn status:', error);
      setLinkedinStatus({ connected: false });
    }
  };

  const loadKeywords = async () => {
    try {
      const res = await fetch(`${api}/api/linkedin/keywords`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setKeywords(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading keywords:', error);
      setKeywords([]);
    }
  };

  const loadPosts = async () => {
    try {
      const res = await fetch(`${api}/api/linkedin/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading posts:', error);
      setPosts([]);
    }
  };

  const loadAllPosts = async () => {
    try {
      const res = await fetch(`${api}/api/admin/linkedin/posts-all`);
      const data = await res.json();
      setAllPosts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading all posts:', error);
      setAllPosts([]);
    }
  };

  const loadSchedules = async () => {
    try {
      const res = await fetch(`${api}/api/linkedin/schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        setSchedules([]);
        return;
      }
      const data = await res.json();
      setSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading schedules:', error);
      setSchedules([]);
    }
  };

  const loadPreferences = async () => {
    try {
      const res = await fetch(`${api}/api/linkedin/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        return;
      }
      const data = await res.json();
      // Populate form with loaded preferences
      if (data) {
        setPrefForm({
          default_tone: data.default_tone || 'professional',
          default_length: data.default_length || 'medium',
          default_hashtag_count: data.default_hashtag_count || 5,
          enable_emojis: data.enable_emojis !== undefined ? data.enable_emojis : true,
          enable_multi_variation: data.enable_multi_variation !== undefined ? data.enable_multi_variation : true,
          variation_count: data.variation_count || 3,
          auto_approve: data.auto_approve || false,
          notification_enabled: data.notification_enabled !== undefined ? data.notification_enabled : true,
          notification_email: data.notification_email || '',
          timezone: data.timezone || 'Asia/Kolkata',
          posting_hours_start: data.posting_hours_start || '09:00',
          posting_hours_end: data.posting_hours_end || '18:00',
          max_posts_per_day: data.max_posts_per_day || 3
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const res = await fetch(`${api}/api/linkedin/analytics/dashboard?period=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        console.error('Analytics API error:', res.status);
        setAnalytics({ summary: { total_posts: 0, total_likes: 0, total_comments: 0, total_shares: 0, total_impressions: 0, avg_engagement_rate: 0 }, topPosts: [], timeline: [] });
        return;
      }
      const data = await res.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setAnalytics({ summary: { total_posts: 0, total_likes: 0, total_comments: 0, total_shares: 0, total_impressions: 0, avg_engagement_rate: 0 }, topPosts: [], timeline: [] });
    }
  };

  const connectLinkedIn = async () => {
    try {
      const res = await fetch(`${api}/api/linkedin/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      setError('Failed to get LinkedIn authorization URL. Please check server configuration.');
    }
  };

  const addKeyword = async (e) => {
    e.preventDefault();
    if (!newKeyword.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${api}/api/linkedin/keywords`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          keyword: newKeyword,
          category: newCategory
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess('Keyword added successfully!');
        setNewKeyword('');
        setNewCategory('');
        setShowAddKeyword(false);
        loadKeywords();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to add keyword');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteKeyword = async (id) => {
    if (!window.confirm('Are you sure you want to delete this keyword?')) return;

    try {
      const res = await fetch(`${api}/api/linkedin/keywords/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setSuccess('Keyword deleted successfully!');
        loadKeywords();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to delete keyword');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const generatePost = async (keywordId) => {
    setLoading(true);
    setError(null);
    setGeneratedImages([]);

    try {
      const res = await fetch(`${api}/api/linkedin/posts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          keywordId,
          tone,
          length,
          withVariations,
          withImages
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess('LinkedIn post generated successfully!');
        setShowGenerateModal(null);
        setActiveTab('posts');

        // Store images if any were returned
        if (data.images && data.images.length > 0) {
          setGeneratedImages(data.images);
        }

        loadPosts();
        loadAllPosts();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || data.message || 'Failed to generate post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const approvePost = async (id) => {
    try {
      const res = await fetch(`${api}/api/linkedin/posts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'approved' })
      });

      if (res.ok) {
        setSuccess('Post approved! It will be published shortly.');
        loadPosts();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to approve post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  // Search images for a specific post using smart search with content analysis
  const searchImagesForPost = async (keyword, postContent = null) => {
    setLoadingImages(true);
    setGeneratedImages([]);

    try {
      // Use smart search API that analyzes content for better image matches
      const res = await fetch(`${api}/api/linkedin/images/smart-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          keyword,
          content: postContent,
          limit: 12
        })
      });

      const data = await res.json();
      if (res.ok && Array.isArray(data) && data.length > 0) {
        setGeneratedImages(data);
      } else {
        // Fall back to basic search if smart search returns no results
        console.log('Smart search returned no results, trying basic search...');
        const fallbackRes = await fetch(`${api}/api/linkedin/images/search?query=${encodeURIComponent(keyword + ' professional')}&limit=12`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const fallbackData = await fallbackRes.json();
        if (fallbackRes.ok && Array.isArray(fallbackData) && fallbackData.length > 0) {
          setGeneratedImages(fallbackData);
        } else {
          setError('No images found for this topic. Try generating with a different keyword.');
          setTimeout(() => setError(null), 5000);
        }
      }
    } catch (err) {
      console.error('Error searching images:', err);
      // Try basic search as fallback
      try {
        const fallbackRes = await fetch(`${api}/api/linkedin/images/search?query=${encodeURIComponent(keyword)}&limit=12`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const fallbackData = await fallbackRes.json();
        if (fallbackRes.ok && Array.isArray(fallbackData) && fallbackData.length > 0) {
          setGeneratedImages(fallbackData);
        } else {
          setError('Failed to load images. Please try again.');
          setTimeout(() => setError(null), 5000);
        }
      } catch (e) {
        console.error('Fallback image search also failed:', e);
        setError('Image search failed. Please check your connection.');
        setTimeout(() => setError(null), 5000);
      }
    } finally {
      setLoadingImages(false);
    }
  };

  const updatePost = async (id, updates) => {
    try {
      const res = await fetch(`${api}/api/linkedin/posts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (res.ok) {
        setSuccess('Post updated successfully!');
        setShowEditModal(null);
        loadPosts();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to update post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const res = await fetch(`${api}/api/linkedin/posts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setSuccess('Post deleted successfully!');
        loadPosts();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to delete post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const approvePostByToken = async (token) => {
    if (!window.confirm('Approve this post and publish to LinkedIn?')) return;

    try {
      const res = await fetch(`${api}/api/linkedin/approve/${token}`);
      if (res.ok) {
        setSuccess('Post approved! It will be published to LinkedIn within 1-2 minutes.');
        loadAllPosts();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Error approving post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const rejectPostByToken = async (token) => {
    if (!window.confirm('Reject this post?')) return;

    try {
      const res = await fetch(`${api}/api/linkedin/reject/${token}`);
      if (res.ok) {
        setSuccess('Post rejected');
        loadAllPosts();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Error rejecting post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const resendApprovalEmail = async (postId) => {
    try {
      const res = await fetch(`${api}/api/linkedin/posts/${postId}/resend-email`, {
        method: 'POST'
      });
      if (res.ok) {
        setSuccess('Approval email sent to aneesh225819@gmail.com');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const error = await res.json();
        setError('Error sending email: ' + (error.error || 'Unknown error'));
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  // Delete single post from admin (Manage All Posts)
  const deleteAdminPost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const res = await fetch(`${api}/api/admin/linkedin/posts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setSuccess('Post deleted successfully!');
        loadAllPosts();
        setSelectedPosts(prev => prev.filter(postId => postId !== id));
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to delete post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  // Bulk delete posts
  const bulkDeletePosts = async () => {
    if (selectedPosts.length === 0) {
      setError('No posts selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedPosts.length} selected post(s)?`)) return;

    setLoading(true);
    try {
      const res = await fetch(`${api}/api/admin/linkedin/posts/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ postIds: selectedPosts })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(`${data.deletedCount} post(s) deleted successfully!`);
        setSelectedPosts([]);
        loadAllPosts();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to delete posts');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle post selection
  const togglePostSelection = (postId) => {
    setSelectedPosts(prev =>
      prev.includes(postId)
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  // Select all posts
  const selectAllPosts = () => {
    if (selectedPosts.length === allPosts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(allPosts.map(post => post.id));
    }
  };

  // Generate post title from content
  const getPostTitle = (content) => {
    if (!content) return 'Untitled Post';
    // Get first line or first 60 characters
    const firstLine = content.split('\n')[0];
    if (firstLine.length > 60) {
      return firstLine.substring(0, 60) + '...';
    }
    return firstLine || 'Untitled Post';
  };

  const addSchedule = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${api}/api/linkedin/schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          keyword_id: scheduleKeywordId,
          interval_type: intervalType,
          interval_value: intervalValue,
          time_of_day: useRandomTime ? null : timeOfDay,
          use_random_time: useRandomTime,
          random_time_start: useRandomTime ? randomTimeStart : null,
          random_time_end: useRandomTime ? randomTimeEnd : null,
          selected_days: intervalType === 'weekly' ? selectedDays : null,
          timezone: timezone
        })
      });

      if (res.ok) {
        setSuccess('Schedule created successfully!');
        setShowScheduleModal(false);
        loadSchedules();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to create schedule');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateSchedule = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${api}/api/linkedin/schedules/${showEditScheduleModal.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          keyword_id: scheduleKeywordId,
          interval_type: intervalType,
          interval_value: intervalValue,
          time_of_day: useRandomTime ? null : timeOfDay,
          use_random_time: useRandomTime,
          random_time_start: useRandomTime ? randomTimeStart : null,
          random_time_end: useRandomTime ? randomTimeEnd : null,
          selected_days: intervalType === 'weekly' ? selectedDays : null,
          timezone: timezone,
          is_active: showEditScheduleModal.is_active
        })
      });

      if (res.ok) {
        setSuccess('Schedule updated successfully!');
        setShowEditScheduleModal(null);
        loadSchedules();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to update schedule');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;

    try {
      const res = await fetch(`${api}/api/linkedin/schedules/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setSuccess('Schedule deleted!');
        loadSchedules();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to delete schedule');
    }
  };

  const savePreferences = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${api}/api/linkedin/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(prefForm)
      });

      if (res.ok) {
        setSuccess('Preferences saved successfully!');
        loadPreferences();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save preferences');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addSuggestedKeyword = async (keyword) => {
    setNewKeyword(keyword);
    setLoading(true);

    try {
      const res = await fetch(`${api}/api/linkedin/keywords`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ keyword, category: 'suggested' })
      });

      if (res.ok) {
        setSuccess(`Added keyword: ${keyword}`);
        loadKeywords();
        setNewKeyword('');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to add keyword');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate trending keyword suggestions with rotation
  const getCurrentYear = () => new Date().getFullYear();
  const getNextYear = () => new Date().getFullYear() + 1;

  const getAllKeywordSuggestions = () => {
    const currentYear = getCurrentYear();
    const nextYear = getNextYear();
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const currentQuarter = Math.ceil(currentMonth / 3); // Q1-Q4

    return [
      // DevOps - Rotating based on trends
      `Kubernetes best practices ${currentYear}`,
      'GitOps workflow automation',
      'CI/CD pipeline optimization',
      'Infrastructure as Code with Terraform',
      'Docker container security',
      'DevSecOps implementation strategies',
      'Microservices architecture patterns',
      'ArgoCD deployment strategies',
      'Jenkins vs GitHub Actions comparison',
      'DevOps monitoring and observability',
      `Platform engineering trends ${currentYear}`,
      'Progressive delivery and feature flags',
      'Chaos engineering in production',
      'DevOps automation with AI',
      'Container orchestration at scale',

      // Cloud - Time-sensitive topics
      `AWS re:Invent ${currentYear} highlights`,
      'Azure cloud migration strategies',
      'GCP serverless architecture',
      'Multi-cloud management best practices',
      'Cloud-native application development',
      'Kubernetes on AWS EKS',
      'Azure DevOps vs AWS DevOps',
      'Cloud security compliance GDPR',
      'Serverless computing benefits',
      'Cloud infrastructure automation',
      `Cloud cost trends Q${currentQuarter} ${currentYear}`,
      'Edge computing and CDN optimization',
      'Hybrid cloud architecture patterns',
      'Cloud disaster recovery planning',
      'Containerization vs serverless',

      // AI & ML - Latest trends
      'AI/ML model deployment in production',
      'Large Language Models (LLMs) in DevOps',
      `AI trends for developers ${currentYear}`,
      'AI-powered monitoring and alerting',
      'Machine Learning pipelines on Kubernetes',
      'Generative AI use cases for enterprises',
      'AI code review and pair programming',
      'MLOps best practices',
      'AI-driven incident response',
      'Prompt engineering for developers',
      'RAG (Retrieval Augmented Generation)',
      'Vector databases for AI applications',
      'Fine-tuning LLMs for business',
      'AI agents and autonomous workflows',
      'Ethical AI and responsible deployment',

      // FinOps - Cost optimization
      'FinOps cloud cost management',
      'AWS cost allocation and tagging',
      'Cloud spending optimization',
      'Reserved instances vs spot instances',
      'Cloud cost monitoring dashboards',
      `FinOps best practices ${currentYear}`,
      'Kubernetes cost optimization',
      'Cloud waste reduction strategies',
      'Showback vs chargeback models',
      'Cloud budget forecasting',

      // Security - Current threats
      'Zero-trust security architecture',
      'Container vulnerability scanning',
      'Secrets management in Kubernetes',
      'SIEM integration strategies',
      'Cloud security posture management (CSPM)',
      `Cybersecurity trends ${currentYear}`,
      'Supply chain security for containers',
      'Identity and access management (IAM)',
      'Security scanning in CI/CD pipelines',
      'Compliance automation (SOC 2, ISO 27001)',
      'Runtime security monitoring',
      'Shift-left security practices',

      // Trending Technologies
      'Platform engineering teams',
      'Internal developer platforms (IDPs)',
      'GitLab CI/CD workflows',
      'Helm charts best practices',
      'Service mesh with Istio',
      'OpenTelemetry implementation',
      'Backstage developer portal',
      'Crossplane infrastructure automation',
      `WebAssembly (WASM) trends ${currentYear}`,
      'eBPF for observability',
      'GitOps with Flux vs ArgoCD',
      'Dapr microservices runtime',
      'Pulumi vs Terraform comparison',
      'Cilium networking and security',

      // Quarterly rotating topics
      ...(currentQuarter === 1 ? [
        `New Year DevOps goals ${currentYear}`,
        'Cloud migration planning strategies',
        'Q1 infrastructure planning'
      ] : []),
      ...(currentQuarter === 2 ? [
        'Mid-year cloud optimization review',
        'Summer DevOps conference takeaways',
        'Q2 security audit best practices'
      ] : []),
      ...(currentQuarter === 3 ? [
        'Fall technology refresh planning',
        `Preparing for ${nextYear} cloud trends`,
        'Q3 cost optimization strategies'
      ] : []),
      ...(currentQuarter === 4 ? [
        `Top DevOps trends for ${nextYear}`,
        'Year-end cloud cost review',
        'Q4 infrastructure consolidation'
      ] : []),

      // Month-specific hot topics
      ...(currentMonth >= 11 || currentMonth <= 1 ? [
        `DevOps predictions for ${nextYear}`,
        'Year-end retrospectives and planning'
      ] : []),
      ...(currentMonth >= 3 && currentMonth <= 5 ? [
        'Spring cleaning your cloud infrastructure',
        'KubeCon conference highlights'
      ] : []),
      ...(currentMonth >= 6 && currentMonth <= 8 ? [
        'AWS Summit key announcements',
        'Summer infrastructure optimization'
      ] : []),
      ...(currentMonth >= 9 && currentMonth <= 11 ? [
        `Preparing for AWS re:Invent ${currentYear}`,
        'Fall conference season insights'
      ] : [])
    ];
  };

  // Rotate suggestions - show random 25 each time
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [suggestionRotation, setSuggestionRotation] = useState(0);

  useEffect(() => {
    rotateKeywordSuggestions();
    // Auto-rotate every 5 minutes
    const rotationInterval = setInterval(() => {
      rotateKeywordSuggestions();
    }, 5 * 60 * 1000);

    return () => clearInterval(rotationInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rotateKeywordSuggestions = () => {
    const allSuggestions = getAllKeywordSuggestions();
    // Shuffle array and take first 25
    const shuffled = [...allSuggestions].sort(() => Math.random() - 0.5);
    setKeywordSuggestions(shuffled.slice(0, 25));
    setSuggestionRotation(prev => prev + 1);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-700',
      pending_approval: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      posted: 'bg-blue-100 text-blue-700',
      failed: 'bg-red-100 text-red-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <MessageSquare className="w-10 h-10" />
              <h1 className="text-4xl font-bold">LinkedIn Content Automation</h1>
            </div>
            <p className="text-blue-100 text-lg">
              Generate engaging LinkedIn posts with AI. Manage keywords and create content effortlessly.
            </p>
          </div>
          <div className="text-right">
            {linkedinStatus.connected ? (
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-300 mb-1">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Connected</span>
                </div>
                <p className="text-sm text-white/90">{linkedinStatus.profileName}</p>
              </div>
            ) : (
              <button
                onClick={connectLinkedIn}
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2"
              >
                <LinkIcon className="w-5 h-5" />
                Connect LinkedIn
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-900 font-medium">{success}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center gap-3">
          <X className="w-5 h-5 text-red-600" />
          <span className="text-red-900 font-medium">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b-2 border-gray-200 overflow-x-auto">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
            activeTab === 'overview'
              ? 'border-b-4 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <BarChart3 className="w-5 h-5 inline mr-2" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab('keywords')}
          className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
            activeTab === 'keywords'
              ? 'border-b-4 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Hash className="w-5 h-5 inline mr-2" />
          Keywords ({keywords.length})
        </button>
        <button
          onClick={() => setActiveTab('posts')}
          className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
            activeTab === 'posts'
              ? 'border-b-4 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Sparkles className="w-5 h-5 inline mr-2" />
          Generated ({posts.length})
        </button>
        <button
          onClick={() => setActiveTab('manage')}
          className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
            activeTab === 'manage'
              ? 'border-b-4 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <CheckCircle className="w-5 h-5 inline mr-2" />
          Manage All ({allPosts.length})
        </button>
        <button
          onClick={() => setActiveTab('schedules')}
          className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
            activeTab === 'schedules'
              ? 'border-b-4 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Calendar className="w-5 h-5 inline mr-2" />
          Schedules ({schedules.length})
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
            activeTab === 'analytics'
              ? 'border-b-4 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <TrendingUp className="w-5 h-5 inline mr-2" />
          Analytics
        </button>
        <button
          onClick={() => setActiveTab('preferences')}
          className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
            activeTab === 'preferences'
              ? 'border-b-4 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Settings className="w-5 h-5 inline mr-2" />
          Settings
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {!analytics && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Loading analytics...</p>
            </div>
          )}
          {analytics && (
            <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 text-sm font-medium">Total Posts</span>
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{analytics.summary.total_posts}</p>
              <p className="text-sm text-green-600 mt-1">{analytics.summary.posted_count} published</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-pink-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 text-sm font-medium">Total Likes</span>
                <ThumbsUp className="w-5 h-5 text-pink-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{analytics.summary.total_likes}</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 text-sm font-medium">Comments</span>
                <MessageCircle className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{analytics.summary.total_comments}</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-green-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 text-sm font-medium">Avg Engagement</span>
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{parseFloat(analytics.summary.avg_engagement_rate || 0).toFixed(1)}%</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setShowAddKeyword(true)}
              className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-100 hover:border-blue-300 transition-colors text-left"
            >
              <Plus className="w-8 h-8 text-blue-600 mb-2" />
              <h3 className="font-bold text-lg mb-1">Add Keyword</h3>
              <p className="text-gray-500 text-sm">Create new content topics</p>
            </button>

            <button
              onClick={() => setActiveTab('posts')}
              className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-100 hover:border-blue-300 transition-colors text-left"
            >
              <Sparkles className="w-8 h-8 text-purple-600 mb-2" />
              <h3 className="font-bold text-lg mb-1">Generate Post</h3>
              <p className="text-gray-500 text-sm">Create AI-powered content</p>
            </button>

            <button
              onClick={() => {
                setActiveTab('schedules');
                setShowScheduleModal(true);
              }}
              className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-100 hover:border-blue-300 transition-colors text-left"
            >
              <Calendar className="w-8 h-8 text-green-600 mb-2" />
              <h3 className="font-bold text-lg mb-1">Schedule Posts</h3>
              <p className="text-gray-500 text-sm">Automate your content</p>
            </button>
          </div>

          {/* Top Performing Posts */}
          {analytics.topPosts && analytics.topPosts.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6">
              <h3 className="text-xl font-bold mb-4">Top Performing Posts</h3>
              <div className="space-y-3">
                {analytics.topPosts.map((post) => (
                  <div key={post.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 line-clamp-1">{post.content}</p>
                      <p className="text-xs text-gray-400 mt-1">{post.keyword}</p>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span className="text-pink-600">❤️ {post.likes || 0}</span>
                      <span className="text-purple-600">💬 {post.comments || 0}</span>
                      <span className="text-green-600">📊 {parseFloat(post.engagement_rate || 0).toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
          )}
        </div>
      )}

      {/* Keywords Tab */}
      {activeTab === 'keywords' && (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Manage Keywords</h2>
            <button
              onClick={() => setShowAddKeyword(!showAddKeyword)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Keyword
            </button>
          </div>

          {/* Add Keyword Form */}
          {showAddKeyword && (
            <form onSubmit={addKeyword} className="bg-gray-50 rounded-xl p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Keyword *
                  </label>
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    placeholder="e.g., DevOps Best Practices"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category (Optional)
                  </label>
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="e.g., DevOps, Cloud"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  {loading ? 'Adding...' : 'Add Keyword'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddKeyword(false);
                    setNewKeyword('');
                    setNewCategory('');
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Trending Keyword Suggestions */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6 border-2 border-blue-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">Trending Topics - DevOps, Cloud & AI</h3>
              </div>
              <button
                onClick={rotateKeywordSuggestions}
                className="px-4 py-2 bg-white border-2 border-blue-300 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2"
                title="Refresh suggestions"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600">
                Click to add popular keywords from latest technologies
                {suggestionRotation > 0 && (
                  <span className="ml-2 text-xs text-blue-600 font-medium">
                    • Auto-rotates every 5 min
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500">
                Showing {keywordSuggestions.filter(s => !keywords.some(k => k.keyword.toLowerCase() === s.toLowerCase())).length} of {getAllKeywordSuggestions().length} topics
              </p>
            </div>
            <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
              {keywordSuggestions
                .filter(suggestion => !keywords.some(k => k.keyword.toLowerCase() === suggestion.toLowerCase()))
                .map((suggestion, idx) => (
                <button
                  key={`${suggestion}-${idx}`}
                  onClick={() => addSuggestedKeyword(suggestion)}
                  disabled={loading}
                  className="px-3 py-2 bg-white border-2 border-blue-200 text-gray-700 rounded-lg text-sm hover:bg-blue-100 hover:border-blue-400 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Plus className="w-3 h-3" />
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          {/* Keywords List */}
          {keywords.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Hash className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No keywords yet</p>
              <p className="text-sm">Add keywords to start generating LinkedIn posts</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {keywords.map((keyword) => (
                <div
                  key={keyword.id}
                  className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">{keyword.keyword}</h3>
                      {keyword.category && (
                        <span className="text-sm text-gray-500">{keyword.category}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setGenerateKeywordId(keyword.id);
                          setShowGenerateModal(true);
                        }}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                        title="Generate Post"
                      >
                        <Sparkles className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteKeyword(keyword.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">
                    Usage: {keyword.usage_count || 0} | Added {new Date(keyword.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Posts Tab */}
      {activeTab === 'posts' && (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Generated Posts</h2>
            {keywords.length > 0 && (
              <button
                onClick={() => setShowGenerateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Generate New Post
              </button>
            )}
          </div>

          {/* Image loading or suggestions - show when a post is selected for image picking */}
          {imagePickerForPost && (
            <div className="bg-purple-50 border-2 border-purple-400 rounded-xl p-6 mb-6 shadow-lg">
              <h3 className="text-lg font-bold text-purple-900 mb-3 flex items-center gap-2">
                <Image className="w-5 h-5 text-purple-600" />
                {loadingImages ? 'Loading Images...' : 'Select an Image for Your Post'}
              </h3>
              {loadingImages ? (
                <div className="text-center py-8">
                  <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-purple-700">Searching for relevant images...</p>
                </div>
              ) : generatedImages.length > 0 ? (
                <>
                  <p className="text-sm text-purple-700 mb-4 bg-purple-100 px-3 py-2 rounded-lg inline-block">
                    Click any image below to add it to your selected post
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {generatedImages.map((img, idx) => (
                      <div
                        key={img.id || idx}
                        className="relative group cursor-pointer rounded-lg overflow-hidden border-2 border-purple-300 hover:border-purple-500 hover:shadow-lg transition-all"
                        onClick={() => {
                          updatePost(imagePickerForPost, { image_url: img.previewUrl || img.thumbnailUrl });
                          setImagePickerForPost(null);
                          setSuccess('Image added to post!');
                          setTimeout(() => setSuccess(null), 3000);
                        }}
                      >
                        <img
                          src={img.thumbnailUrl || img.previewUrl}
                          alt={img.title || 'Suggested image'}
                          className="w-full h-24 object-cover"
                        />
                        <div className="absolute inset-0 bg-purple-600 bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                          <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-bold">
                            Select
                          </span>
                        </div>
                        {img.premium && (
                          <span className="absolute top-1 right-1 bg-yellow-500 text-white text-xs px-1 rounded">PRO</span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <p>No images found. Try a different keyword.</p>
                </div>
              )}
              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={() => {
                    setImagePickerForPost(null);
                    setGeneratedImages([]);
                  }}
                  className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                >
                  Cancel
                </button>
                {generatedImages.length > 0 && (
                  <button
                    onClick={() => {
                      setGeneratedImages([]);
                      // Re-search with the same post
                      const post = posts.find(p => p.id === imagePickerForPost);
                      if (post) searchImagesForPost(post.keyword, post.content);
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Search Again
                  </button>
                )}
              </div>
            </div>
          )}

          {posts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Sparkles className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No posts generated yet</p>
              <p className="text-sm mb-6">Generate your first LinkedIn post from a keyword</p>
              {keywords.length > 0 ? (
                <button
                  onClick={() => setShowGenerateModal(true)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  Generate Your First Post
                </button>
              ) : (
                <div>
                  <p className="text-sm text-gray-400 mb-3">You need to add keywords first</p>
                  <button
                    onClick={() => setActiveTab('keywords')}
                    className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors inline-flex items-center gap-2"
                  >
                    <Hash className="w-5 h-5" />
                    Add Keywords
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className={`rounded-xl p-6 transition-all ${
                    imagePickerForPost === post.id
                      ? 'bg-purple-50 border-2 border-purple-400 ring-2 ring-purple-200'
                      : 'bg-gray-50 border-2 border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                        <Hash className="w-5 h-5 text-blue-600" />
                        {post.keyword}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Generated {new Date(post.created_at).toLocaleString()}
                      </p>
                      <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(post.status)}`}>
                        {post.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {post.status === 'draft' && (
                        <button
                          onClick={() => approvePost(post.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                      )}
                      <button
                        onClick={() => copyToClipboard(post.content)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        Copy
                      </button>
                      <button
                        onClick={() => {
                          if (imagePickerForPost === post.id) {
                            setImagePickerForPost(null);
                          } else {
                            setImagePickerForPost(post.id);
                            // Always trigger smart search with post content for better image relevance
                            searchImagesForPost(post.keyword, post.content);
                          }
                        }}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                          imagePickerForPost === post.id
                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        }`}
                      >
                        <Image className="w-4 h-4" />
                        {imagePickerForPost === post.id ? 'Cancel' : (post.image_url ? 'Change Image' : 'Add Image')}
                      </button>
                      <button
                        onClick={() => setShowEditModal(post)}
                        className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deletePost(post.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Post Image if exists */}
                  {post.image_url && (
                    <div className="mb-4">
                      <img
                        src={post.image_url}
                        alt="Post image"
                        className="max-w-md rounded-lg border border-gray-200"
                      />
                    </div>
                  )}

                  <div className="bg-white rounded-lg p-4 border border-gray-200 whitespace-pre-wrap text-gray-800">
                    {post.content}
                  </div>

                  <div className="mt-4 flex gap-4 text-sm text-gray-500">
                    <span>Tone: {post.tone}</span>
                    <span>Length: {post.length}</span>
                    {post.variation_count > 0 && <span>Variations: {post.variation_count}</span>}
                  </div>

                  {/* Analytics */}
                  {post.analytics && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                          <Eye className="w-3 h-3" />
                          <span>Views</span>
                        </div>
                        <p className="text-lg font-bold">{post.analytics.impressions || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 text-pink-500 text-xs mb-1">
                          <ThumbsUp className="w-3 h-3" />
                          <span>Likes</span>
                        </div>
                        <p className="text-lg font-bold">{post.analytics.likes || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 text-purple-500 text-xs mb-1">
                          <MessageCircle className="w-3 h-3" />
                          <span>Comments</span>
                        </div>
                        <p className="text-lg font-bold">{post.analytics.comments || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 text-blue-500 text-xs mb-1">
                          <Share2 className="w-3 h-3" />
                          <span>Shares</span>
                        </div>
                        <p className="text-lg font-bold">{post.analytics.shares || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 text-green-500 text-xs mb-1">
                          <TrendingUp className="w-3 h-3" />
                          <span>Engagement</span>
                        </div>
                        <p className="text-lg font-bold">{parseFloat(post.analytics.engagement_rate || 0).toFixed(1)}%</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Manage All Posts Tab */}
      {activeTab === 'manage' && (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Manage All Posts</h2>
              <p className="text-sm text-gray-500 mt-1">View and manage all LinkedIn posts across all statuses</p>
            </div>
            {allPosts.length > 0 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={selectAllPosts}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
                >
                  {selectedPosts.length === allPosts.length ? (
                    <CheckSquare className="w-4 h-4 text-blue-600" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  {selectedPosts.length === allPosts.length ? 'Deselect All' : 'Select All'}
                </button>
                {selectedPosts.length > 0 && (
                  <button
                    onClick={bulkDeletePosts}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center gap-2 disabled:bg-gray-400"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Selected ({selectedPosts.length})
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Image picker for Manage All Posts */}
          {imagePickerForPost && (
            <div className="mb-6 bg-purple-50 rounded-xl p-6 border-2 border-purple-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-purple-900">Select an Image</h3>
                <button
                  onClick={() => setImagePickerForPost(null)}
                  className="text-purple-600 hover:text-purple-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {loadingImages ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-purple-700">Searching for images...</span>
                </div>
              ) : generatedImages.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {generatedImages.map((img, idx) => (
                    <div
                      key={idx}
                      className="relative group cursor-pointer rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-500 transition-all"
                      onClick={() => {
                        updatePost(imagePickerForPost, { image_url: img.previewUrl || img.thumbnailUrl });
                        setImagePickerForPost(null);
                        setSuccess('Image added to post!');
                        setTimeout(() => setSuccess(null), 3000);
                      }}
                    >
                      <img
                        src={img.thumbnailUrl || img.previewUrl}
                        alt={img.title || 'Image option'}
                        className="w-full h-32 object-cover"
                      />
                      <div className="absolute inset-0 bg-purple-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-all" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No images found. Try a different keyword.</p>
                </div>
              )}
            </div>
          )}

          {allPosts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No posts found</p>
              <p className="text-sm">Posts will appear here once generated</p>
            </div>
          ) : (
            <div className="space-y-2">
              {allPosts.map((post) => (
                <div
                  key={post.id}
                  className={`border-2 rounded-xl overflow-hidden transition-all ${
                    selectedPosts.includes(post.id) ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Collapsed View - Title Row */}
                  <div
                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedPostId(expandedPostId === post.id ? null : post.id)}
                  >
                    {/* Checkbox */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePostSelection(post.id);
                      }}
                      className="cursor-pointer"
                    >
                      {selectedPosts.includes(post.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </div>

                    {/* Status Badge */}
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusBadge(post.status)}`}>
                      {post.status.replace('_', ' ').toUpperCase()}
                    </span>

                    {/* Title */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {getPostTitle(post.content)}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                        {post.keyword && (
                          <span className="flex items-center gap-1">
                            <Hash className="w-3 h-3" />
                            {post.keyword}
                          </span>
                        )}
                        <span>•</span>
                        <span>{new Date(post.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(post.content);
                        }}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Copy content"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteAdminPost(post.id);
                        }}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Delete post"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expandedPostId === post.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded View - Full Content */}
                  {expandedPostId === post.id && (
                    <div className="border-t-2 border-gray-100 p-4 bg-gray-50">
                      {/* Post Info */}
                      <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-600">
                        <span>User: {post.user_email}</span>
                        <span>Tone: {post.tone || 'N/A'}</span>
                        <span>Length: {post.length || 'N/A'}</span>
                        {post.posted_at && (
                          <span>Posted: {new Date(post.posted_at).toLocaleString()}</span>
                        )}
                      </div>

                      {/* Full Content */}
                      <div className="bg-white rounded-lg p-4 border border-gray-200 whitespace-pre-wrap text-gray-800 mb-4">
                        {post.content}
                      </div>

                      {/* Image if exists */}
                      {post.image_url && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Attached Image:</p>
                          <img
                            src={post.image_url}
                            alt="Post image"
                            className="max-w-md rounded-lg border border-gray-200"
                          />
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        {post.status === 'pending_approval' && (
                          <>
                            <button
                              onClick={() => approvePostByToken(post.approval_token)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve & Post
                            </button>
                            <button
                              onClick={() => resendApprovalEmail(post.id)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                              <Send className="w-4 h-4" />
                              Resend Email
                            </button>
                            <button
                              onClick={() => rejectPostByToken(post.approval_token)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center gap-2"
                            >
                              <X className="w-4 h-4" />
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => {
                            if (imagePickerForPost === post.id) {
                              setImagePickerForPost(null);
                            } else {
                              setImagePickerForPost(post.id);
                              searchImagesForPost(post.keyword, post.content);
                            }
                          }}
                          className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                            imagePickerForPost === post.id
                              ? 'bg-purple-600 text-white hover:bg-purple-700'
                              : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          }`}
                        >
                          <Image className="w-4 h-4" />
                          {imagePickerForPost === post.id ? 'Cancel' : (post.image_url ? 'Change Image' : 'Add Image')}
                        </button>
                        <button
                          onClick={() => copyToClipboard(post.content)}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors flex items-center gap-2"
                        >
                          <Copy className="w-4 h-4" />
                          Copy Content
                        </button>
                        <button
                          onClick={() => deleteAdminPost(post.id)}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-colors flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Post
                        </button>
                      </div>

                      {/* LinkedIn Link */}
                      {post.linkedin_post_url && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <a
                            href={post.linkedin_post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
                          >
                            <LinkIcon className="w-4 h-4" />
                            View on LinkedIn
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Schedules Tab */}
      {activeTab === 'schedules' && (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Automation Schedules</h2>
            <button
              onClick={() => setShowScheduleModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Schedule
            </button>
          </div>

          {schedules.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No schedules yet</p>
              <p className="text-sm">Create schedules to automate post generation</p>
            </div>
          ) : (
            <div className="space-y-4">
              {schedules.map((schedule) => (
                <div key={schedule.id} className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{schedule.keyword}</h3>
                      <div className="flex gap-4 mt-2 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {schedule.interval_type} ({schedule.interval_value})
                        </span>
                        {schedule.time_of_day && <span>at {schedule.time_of_day}</span>}
                        {schedule.use_random_time && (
                          <span className="text-blue-600">🎲 Random time ({schedule.random_time_start} - {schedule.random_time_end})</span>
                        )}
                        <span>Next: {new Date(schedule.next_run_at).toLocaleString()}</span>
                      </div>
                      {schedule.selected_days && schedule.selected_days.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                            <span
                              key={idx}
                              className={`text-xs px-2 py-1 rounded ${
                                schedule.selected_days.includes(idx)
                                  ? 'bg-blue-100 text-blue-700 font-semibold'
                                  : 'bg-gray-100 text-gray-400'
                              }`}
                            >
                              {day}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        Total runs: {schedule.total_runs} | Last run: {schedule.last_run_at ? new Date(schedule.last_run_at).toLocaleString() : 'Never'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setShowEditScheduleModal(schedule);
                          setScheduleKeywordId(schedule.keyword_id);
                          setIntervalType(schedule.interval_type);
                          setIntervalValue(schedule.interval_value || 1);
                          setTimeOfDay(schedule.time_of_day || '09:00');
                          setUseRandomTime(schedule.use_random_time || false);
                          setRandomTimeStart(schedule.random_time_start || '09:00');
                          setRandomTimeEnd(schedule.random_time_end || '17:00');
                          setSelectedDays(schedule.selected_days || [1, 2, 3, 4, 5]);
                          setTimezone(schedule.timezone || 'Asia/Kolkata');
                        }}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                        title="Edit Schedule"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteSchedule(schedule.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        title="Delete Schedule"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {!analytics && (
            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading analytics...</p>
              </div>
            </div>
          )}
          {analytics && (
            <>
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Performance Analytics (Last 30 Days)</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">{analytics.summary.total_impressions}</p>
                <p className="text-sm text-gray-600 mt-1">Total Impressions</p>
              </div>
              <div className="text-center p-4 bg-pink-50 rounded-lg">
                <p className="text-3xl font-bold text-pink-600">{analytics.summary.total_likes}</p>
                <p className="text-sm text-gray-600 mt-1">Total Likes</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-3xl font-bold text-purple-600">{analytics.summary.total_comments}</p>
                <p className="text-sm text-gray-600 mt-1">Total Comments</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{analytics.summary.total_shares}</p>
                <p className="text-sm text-gray-600 mt-1">Total Shares</p>
              </div>
            </div>

            {analytics.topPosts && analytics.topPosts.length > 0 && (
              <div>
                <h3 className="text-lg font-bold mb-4">Best Performing Content</h3>
                <div className="space-y-3">
                  {analytics.topPosts.map((post, idx) => (
                    <div key={post.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-300">#{idx + 1}</div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 mb-2 line-clamp-2">{post.content}</p>
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>👁️ {post.impressions || 0}</span>
                          <span>❤️ {post.likes || 0}</span>
                          <span>💬 {post.comments || 0}</span>
                          <span>🔄 {post.shares || 0}</span>
                          <span className="font-semibold text-green-600">📊 {parseFloat(post.engagement_rate || 0).toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          </>
          )}
        </div>
      )}

      {/* Settings/Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Automation Settings</h2>
            <p className="text-gray-600 mt-2">Customize how your LinkedIn posts are generated and published</p>
          </div>

          <form onSubmit={savePreferences} className="space-y-8">
            {/* Content Generation Settings */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-600" />
                Content Generation Defaults
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 rounded-xl p-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Default Tone
                  </label>
                  <select
                    value={prefForm.default_tone}
                    onChange={(e) => setPrefForm({...prefForm, default_tone: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  >
                    <option value="professional">Professional</option>
                    <option value="casual">Casual</option>
                    <option value="inspiring">Inspiring</option>
                    <option value="educational">Educational</option>
                    <option value="storytelling">Storytelling</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Default Length
                  </label>
                  <select
                    value={prefForm.default_length}
                    onChange={(e) => setPrefForm({...prefForm, default_length: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  >
                    <option value="short">Short (100-150 words)</option>
                    <option value="medium">Medium (150-250 words)</option>
                    <option value="long">Long (250-400 words)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Default Hashtag Count
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={prefForm.default_hashtag_count}
                    onChange={(e) => setPrefForm({...prefForm, default_hashtag_count: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Number of hashtags to include (0-10)</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Variation Count (A/B Testing)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={prefForm.variation_count}
                    onChange={(e) => setPrefForm({...prefForm, variation_count: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Generate multiple variations per post</p>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="enable_emojis"
                    checked={prefForm.enable_emojis}
                    onChange={(e) => setPrefForm({...prefForm, enable_emojis: e.target.checked})}
                    className="w-5 h-5 text-blue-600"
                  />
                  <label htmlFor="enable_emojis" className="text-sm font-medium text-gray-700">
                    Enable emojis in posts
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="enable_multi_variation"
                    checked={prefForm.enable_multi_variation}
                    onChange={(e) => setPrefForm({...prefForm, enable_multi_variation: e.target.checked})}
                    className="w-5 h-5 text-blue-600"
                  />
                  <label htmlFor="enable_multi_variation" className="text-sm font-medium text-gray-700">
                    Enable multi-variation generation
                  </label>
                </div>
              </div>
            </div>

            {/* Posting Settings */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-green-600" />
                Posting Schedule Settings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 rounded-xl p-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Timezone
                  </label>
                  <select
                    value={prefForm.timezone}
                    onChange={(e) => setPrefForm({...prefForm, timezone: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  >
                    <option value="Asia/Kolkata">India (IST - UTC+5:30)</option>
                    <option value="America/New_York">New York (EST - UTC-5)</option>
                    <option value="America/Los_Angeles">Los Angeles (PST - UTC-8)</option>
                    <option value="Europe/London">London (GMT - UTC+0)</option>
                    <option value="Europe/Paris">Paris (CET - UTC+1)</option>
                    <option value="Asia/Dubai">Dubai (GST - UTC+4)</option>
                    <option value="Asia/Singapore">Singapore (SGT - UTC+8)</option>
                    <option value="Australia/Sydney">Sydney (AEDT - UTC+11)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Max Posts Per Day
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={prefForm.max_posts_per_day}
                    onChange={(e) => setPrefForm({...prefForm, max_posts_per_day: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Limit automated posts per day</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Posting Hours Start
                  </label>
                  <input
                    type="time"
                    value={prefForm.posting_hours_start}
                    onChange={(e) => setPrefForm({...prefForm, posting_hours_start: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Posting Hours End
                  </label>
                  <input
                    type="time"
                    value={prefForm.posting_hours_end}
                    onChange={(e) => setPrefForm({...prefForm, posting_hours_end: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Approval & Notifications */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                Approval & Notifications
              </h3>
              <div className="space-y-4 bg-gray-50 rounded-xl p-6">
                <div className="flex items-start gap-3 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="auto_approve"
                    checked={prefForm.auto_approve}
                    onChange={(e) => setPrefForm({...prefForm, auto_approve: e.target.checked})}
                    className="w-5 h-5 text-blue-600 mt-0.5"
                  />
                  <div className="flex-1">
                    <label htmlFor="auto_approve" className="text-sm font-semibold text-gray-900 block">
                      Auto-approve posts (not recommended)
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      ⚠️ Posts will be published automatically without your review. Only enable if you trust the AI completely.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="notification_enabled"
                    checked={prefForm.notification_enabled}
                    onChange={(e) => setPrefForm({...prefForm, notification_enabled: e.target.checked})}
                    className="w-5 h-5 text-blue-600"
                  />
                  <label htmlFor="notification_enabled" className="text-sm font-medium text-gray-700">
                    Enable email notifications
                  </label>
                </div>

                {prefForm.notification_enabled && (
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-100">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-semibold text-gray-900">
                        Notification Email
                      </label>
                      {!showEmailEdit && prefForm.notification_email && (
                        <button
                          type="button"
                          onClick={() => setShowEmailEdit(true)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                        >
                          <Edit2 className="w-3 h-3" />
                          Edit
                        </button>
                      )}
                    </div>

                    {!showEmailEdit && prefForm.notification_email ? (
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <MessageSquare className="w-4 h-4 text-blue-600" />
                        <span className="text-gray-800 font-medium">{prefForm.notification_email}</span>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="email"
                          value={prefForm.notification_email}
                          onChange={(e) => setPrefForm({...prefForm, notification_email: e.target.value})}
                          placeholder="your@email.com"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                        />
                        {showEmailEdit && (
                          <button
                            type="button"
                            onClick={() => setShowEmailEdit(false)}
                            className="mt-2 text-sm text-gray-600 hover:text-gray-700"
                          >
                            Cancel editing
                          </button>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Receive notifications for post approvals, publish confirmations, and weekly reports
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-3 pt-6 border-t-2 border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                {loading ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Generate Post Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full my-8">
            <h3 className="text-2xl font-bold mb-6">Generate LinkedIn Post</h3>

            <div className="space-y-4 mb-6">
              {/* Searchable Keyword Dropdown */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Keyword/Topic *
                </label>

                {/* Search Input */}
                <div className="relative mb-3">
                  <input
                    type="text"
                    placeholder="Search keywords..."
                    value={generateKeywordSearch}
                    onChange={(e) => setGenerateKeywordSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                  <Hash className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                </div>

                {/* Selected Keyword Display */}
                {generateKeywordId && (
                  <div className="mb-3 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-blue-900">
                        {keywords.find(k => k.id === generateKeywordId)?.keyword}
                      </span>
                      {keywords.find(k => k.id === generateKeywordId)?.category && (
                        <span className="text-xs px-2 py-1 bg-blue-200 text-blue-700 rounded">
                          {keywords.find(k => k.id === generateKeywordId)?.category}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setGenerateKeywordId('')}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Keyword Cards Grid */}
                <div className="max-h-48 overflow-y-auto border-2 border-gray-200 rounded-lg p-3 bg-gray-50">
                  {keywords.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <Hash className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="font-medium">No keywords yet</p>
                      <p className="text-sm">Add keywords in the Keywords tab first</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2">
                      {keywords
                        .filter(k => k.keyword.toLowerCase().includes(generateKeywordSearch.toLowerCase()))
                        .slice(0, 8)
                        .map((k) => (
                          <button
                            key={k.id}
                            type="button"
                            onClick={() => setGenerateKeywordId(k.id)}
                            className={`text-left p-3 rounded-lg border-2 transition-all ${
                              generateKeywordId === k.id
                                ? 'bg-blue-100 border-blue-500 shadow-md'
                                : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">{k.keyword}</p>
                                {k.category && (
                                  <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded mt-1 inline-block">
                                    {k.category}
                                  </span>
                                )}
                              </div>
                              {generateKeywordId === k.id && (
                                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                              )}
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                  {keywords.filter(k => k.keyword.toLowerCase().includes(generateKeywordSearch.toLowerCase())).length > 8 && (
                    <p className="text-xs text-gray-500 text-center mt-3">
                      Showing 8 of {keywords.filter(k => k.keyword.toLowerCase().includes(generateKeywordSearch.toLowerCase())).length} keywords. Use search to narrow down.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tone</label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="inspiring">Inspiring</option>
                  <option value="educational">Educational</option>
                  <option value="storytelling">Storytelling</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Length</label>
                <select
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value="short">Short (100-150 words)</option>
                  <option value="medium">Medium (150-250 words)</option>
                  <option value="long">Long (250-400 words)</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="withVariations"
                  checked={withVariations}
                  onChange={(e) => setWithVariations(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                <label htmlFor="withVariations" className="text-sm font-medium text-gray-700">
                  Generate 3 variations (A/B testing)
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="withImages"
                  checked={withImages}
                  onChange={(e) => setWithImages(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                <label htmlFor="withImages" className="text-sm font-medium text-gray-700">
                  Include image suggestions
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => generatePost(generateKeywordId)}
                disabled={loading || !generateKeywordId}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                {loading ? 'Generating...' : 'Generate Post'}
              </button>
              <button
                onClick={() => {
                  setShowGenerateModal(null);
                  setGenerateKeywordId('');
                  setGenerateKeywordSearch('');
                }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full my-8">
            <h3 className="text-2xl font-bold mb-6">Create Automated Schedule</h3>
            <form onSubmit={addSchedule} className="space-y-6">
              {/* Keyword Selection - Improved with Search & Cards */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select Keyword/Topic *
                </label>

                {/* Search and Filter */}
                <div className="mb-4 space-y-3">
                  {/* Search Input */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search keywords..."
                      value={keywordSearch}
                      onChange={(e) => setKeywordSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    />
                    <Hash className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                  </div>

                  {/* Category Filter */}
                  <div className="flex gap-2 flex-wrap">
                    {['all', 'DevOps', 'Cloud', 'AI', 'Security', 'FinOps'].map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setKeywordCategoryFilter(cat)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          keywordCategoryFilter === cat
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {cat === 'all' ? 'All Categories' : cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selected Keyword Display */}
                {scheduleKeywordId && (
                  <div className="mb-4 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-blue-900">
                        Selected: {keywords.find(k => k.id === scheduleKeywordId)?.keyword}
                      </span>
                      {keywords.find(k => k.id === scheduleKeywordId)?.category && (
                        <span className="text-xs px-2 py-1 bg-blue-200 text-blue-700 rounded">
                          {keywords.find(k => k.id === scheduleKeywordId)?.category}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setScheduleKeywordId('')}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Keyword Cards Grid */}
                <div className="max-h-64 overflow-y-auto border-2 border-gray-200 rounded-lg p-3 bg-gray-50">
                  {keywords.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Hash className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="font-medium">No keywords yet</p>
                      <p className="text-sm">Add keywords in the Keywords tab first</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2">
                      {keywords
                        .filter(k => {
                          // Search filter
                          const matchesSearch = k.keyword.toLowerCase().includes(keywordSearch.toLowerCase());
                          // Category filter
                          const matchesCategory = keywordCategoryFilter === 'all' ||
                                                  k.category?.toLowerCase() === keywordCategoryFilter.toLowerCase();
                          return matchesSearch && matchesCategory;
                        })
                        .slice(0, 10) // Show max 10 at a time
                        .map((k) => (
                          <button
                            key={k.id}
                            type="button"
                            onClick={() => setScheduleKeywordId(k.id)}
                            className={`text-left p-3 rounded-lg border-2 transition-all ${
                              scheduleKeywordId === k.id
                                ? 'bg-blue-100 border-blue-500 shadow-md'
                                : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">{k.keyword}</p>
                                {k.category && (
                                  <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded mt-1 inline-block">
                                    {k.category}
                                  </span>
                                )}
                              </div>
                              {scheduleKeywordId === k.id && (
                                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                              )}
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                  {keywords.filter(k => {
                    const matchesSearch = k.keyword.toLowerCase().includes(keywordSearch.toLowerCase());
                    const matchesCategory = keywordCategoryFilter === 'all' ||
                                            k.category?.toLowerCase() === keywordCategoryFilter.toLowerCase();
                    return matchesSearch && matchesCategory;
                  }).length > 10 && (
                    <p className="text-xs text-gray-500 text-center mt-3">
                      Showing 10 of {keywords.filter(k => {
                        const matchesSearch = k.keyword.toLowerCase().includes(keywordSearch.toLowerCase());
                        const matchesCategory = keywordCategoryFilter === 'all' ||
                                                k.category?.toLowerCase() === keywordCategoryFilter.toLowerCase();
                        return matchesSearch && matchesCategory;
                      }).length} keywords. Use search to narrow down.
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">Click a keyword card to select it for this schedule</p>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Posting Frequency *
                </label>
                <select
                  value={intervalType}
                  onChange={(e) => setIntervalType(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly (Select specific days)</option>
                  <option value="custom">Custom (Every N hours)</option>
                  <option value="smart">Smart (AI-optimized timing)</option>
                </select>
              </div>

              {/* Daily/Custom Interval Value */}
              {(intervalType === 'daily' || intervalType === 'custom') && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {intervalType === 'daily' ? 'Post every N days' : 'Post every N hours'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={intervalType === 'daily' ? '30' : '168'}
                    value={intervalValue}
                    onChange={(e) => setIntervalValue(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {intervalType === 'daily' ? 'E.g., 1 = every day, 2 = every 2 days' : 'E.g., 24 = once per day, 12 = twice per day'}
                  </p>
                </div>
              )}

              {/* Weekly - Day Selection */}
              {intervalType === 'weekly' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Select Days of Week *
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: 1, label: 'Monday' },
                      { value: 2, label: 'Tuesday' },
                      { value: 3, label: 'Wednesday' },
                      { value: 4, label: 'Thursday' },
                      { value: 5, label: 'Friday' },
                      { value: 6, label: 'Saturday' },
                      { value: 0, label: 'Sunday' }
                    ].map(day => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => {
                          if (selectedDays.includes(day.value)) {
                            setSelectedDays(selectedDays.filter(d => d !== day.value));
                          } else {
                            setSelectedDays([...selectedDays, day.value]);
                          }
                        }}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          selectedDays.includes(day.value)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {day.label.substring(0, 3)}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Selected: {selectedDays.length === 0 ? 'None' : selectedDays.length === 7 ? 'Every day' : `${selectedDays.length} days`}
                  </p>
                </div>
              )}

              {/* Time Settings */}
              {intervalType !== 'smart' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="useRandomTime"
                      checked={useRandomTime}
                      onChange={(e) => setUseRandomTime(e.target.checked)}
                      className="w-5 h-5 text-blue-600"
                    />
                    <label htmlFor="useRandomTime" className="text-sm font-semibold text-gray-700 flex-1">
                      Use random posting time (looks more natural/manual)
                    </label>
                  </div>

                  {!useRandomTime ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Fixed Posting Time
                      </label>
                      <input
                        type="time"
                        value={timeOfDay}
                        onChange={(e) => setTimeOfDay(e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">Posts will be created at this exact time every day</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Random Time Start
                        </label>
                        <input
                          type="time"
                          value={randomTimeStart}
                          onChange={(e) => setRandomTimeStart(e.target.value)}
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Random Time End
                        </label>
                        <input
                          type="time"
                          value={randomTimeEnd}
                          onChange={(e) => setRandomTimeEnd(e.target.value)}
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <p className="text-xs text-gray-500 col-span-2 -mt-2">
                        Posts will be created at a random time between {randomTimeStart} and {randomTimeEnd} each day
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Timezone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value="Asia/Kolkata">India (IST - UTC+5:30)</option>
                  <option value="America/New_York">New York (EST - UTC-5)</option>
                  <option value="America/Los_Angeles">Los Angeles (PST - UTC-8)</option>
                  <option value="Europe/London">London (GMT - UTC+0)</option>
                  <option value="Europe/Paris">Paris (CET - UTC+1)</option>
                  <option value="Asia/Dubai">Dubai (GST - UTC+4)</option>
                  <option value="Asia/Singapore">Singapore (SGT - UTC+8)</option>
                  <option value="Australia/Sydney">Sydney (AEDT - UTC+11)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>

              {/* Smart Schedule Info */}
              {intervalType === 'smart' && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">Smart Schedule</h4>
                  <ul className="text-sm text-green-800 space-y-1">
                    <li>• AI will automatically choose optimal posting times</li>
                    <li>• Posts between 9 AM - 6 PM on weekdays</li>
                    <li>• Varies timing to look natural</li>
                    <li>• Maximizes engagement based on LinkedIn best practices</li>
                  </ul>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="submit"
                  disabled={loading || (intervalType === 'weekly' && selectedDays.length === 0)}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Schedule'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowScheduleModal(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Schedule Modal */}
      {showEditScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full my-8">
            <h3 className="text-2xl font-bold mb-6">Edit Schedule</h3>
            <form onSubmit={updateSchedule} className="space-y-6">
              {/* Keyword Selection - Same as create modal */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select Keyword/Topic *
                </label>

                {/* Search and Filter */}
                <div className="mb-4 space-y-3">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search keywords..."
                      value={keywordSearch}
                      onChange={(e) => setKeywordSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    />
                    <Hash className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {['all', 'DevOps', 'Cloud', 'AI', 'Security', 'FinOps'].map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setKeywordCategoryFilter(cat)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          keywordCategoryFilter === cat
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {cat === 'all' ? 'All Categories' : cat}
                      </button>
                    ))}
                  </div>
                </div>

                {scheduleKeywordId && (
                  <div className="mb-4 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-blue-900">
                        Selected: {keywords.find(k => k.id === scheduleKeywordId)?.keyword}
                      </span>
                    </div>
                    <button type="button" onClick={() => setScheduleKeywordId('')} className="text-blue-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <div className="max-h-64 overflow-y-auto border-2 border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="grid grid-cols-1 gap-2">
                    {keywords
                      .filter(k => {
                        const matchesSearch = k.keyword.toLowerCase().includes(keywordSearch.toLowerCase());
                        const matchesCategory = keywordCategoryFilter === 'all' ||
                                                k.category?.toLowerCase() === keywordCategoryFilter.toLowerCase();
                        return matchesSearch && matchesCategory;
                      })
                      .slice(0, 10)
                      .map((k) => (
                        <button
                          key={k.id}
                          type="button"
                          onClick={() => setScheduleKeywordId(k.id)}
                          className={`text-left p-3 rounded-lg border-2 transition-all ${
                            scheduleKeywordId === k.id
                              ? 'bg-blue-100 border-blue-500 shadow-md'
                              : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{k.keyword}</p>
                              {k.category && (
                                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded mt-1 inline-block">
                                  {k.category}
                                </span>
                              )}
                            </div>
                            {scheduleKeywordId === k.id && <CheckCircle className="w-5 h-5 text-blue-600" />}
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              </div>

              {/* Frequency - Same structure as create, all fields same */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Posting Frequency *</label>
                <select value={intervalType} onChange={(e) => setIntervalType(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly (Select specific days)</option>
                  <option value="custom">Custom (Every N hours)</option>
                  <option value="smart">Smart (AI-optimized timing)</option>
                </select>
              </div>

              {(intervalType === 'daily' || intervalType === 'custom') && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {intervalType === 'daily' ? 'Post every N days' : 'Post every N hours'}
                  </label>
                  <input type="number" min="1" max={intervalType === 'daily' ? '30' : '168'} value={intervalValue}
                    onChange={(e) => setIntervalValue(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none" />
                </div>
              )}

              {intervalType === 'weekly' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Select Days of Week *</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[{value: 1, label: 'Monday'}, {value: 2, label: 'Tuesday'}, {value: 3, label: 'Wednesday'},
                      {value: 4, label: 'Thursday'}, {value: 5, label: 'Friday'}, {value: 6, label: 'Saturday'},
                      {value: 0, label: 'Sunday'}].map(day => (
                      <button key={day.value} type="button" onClick={() => {
                        if (selectedDays.includes(day.value)) {
                          setSelectedDays(selectedDays.filter(d => d !== day.value));
                        } else {
                          setSelectedDays([...selectedDays, day.value]);
                        }
                      }} className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedDays.includes(day.value) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                        {day.label.substring(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {intervalType !== 'smart' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                    <input type="checkbox" id="useRandomTimeEdit" checked={useRandomTime}
                      onChange={(e) => setUseRandomTime(e.target.checked)} className="w-5 h-5" />
                    <label htmlFor="useRandomTimeEdit" className="text-sm font-semibold text-gray-700">
                      Use random posting time (looks more natural/manual)
                    </label>
                  </div>

                  {!useRandomTime ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Fixed Posting Time</label>
                      <input type="time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Random Time Start</label>
                        <input type="time" value={randomTimeStart} onChange={(e) => setRandomTimeStart(e.target.value)}
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Random Time End</label>
                        <input type="time" value={randomTimeEnd} onChange={(e) => setRandomTimeEnd(e.target.value)}
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Timezone</label>
                <select value={timezone} onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none">
                  <option value="Asia/Kolkata">India (IST - UTC+5:30)</option>
                  <option value="America/New_York">New York (EST - UTC-5)</option>
                  <option value="America/Los_Angeles">Los Angeles (PST - UTC-8)</option>
                  <option value="Europe/London">London (GMT - UTC+0)</option>
                  <option value="Europe/Paris">Paris (CET - UTC+1)</option>
                  <option value="Asia/Dubai">Dubai (GST - UTC+4)</option>
                  <option value="Asia/Singapore">Singapore (SGT - UTC+8)</option>
                  <option value="Australia/Sydney">Sydney (AEDT - UTC+11)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <button type="submit" disabled={loading}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400">
                  {loading ? 'Updating...' : 'Update Schedule'}
                </button>
                <button type="button" onClick={() => setShowEditScheduleModal(null)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-4">Edit Post</h3>

            <textarea
              value={showEditModal.content}
              onChange={(e) =>
                setShowEditModal({ ...showEditModal, content: e.target.value })
              }
              rows={15}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none font-mono text-sm mb-6"
            />

            <div className="flex gap-3">
              <button
                onClick={() => updatePost(showEditModal.id, { content: showEditModal.content })}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={() => setShowEditModal(null)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
